import { type FC, useCallback, useMemo, useState } from "react";
import { DataTestIds } from "src/shared/constants/TestEnums";
import { ChevronDownIcon, LocationOutlineIcon } from "@stubhub/broadway-web/icons/functional";
import { useFeatureTernary } from "@viagogo/feature";
import { useFormattedContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { type Location, LocationSourceType } from "src/types/shared/cookies";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { LocationSelector } from "src/website-apps/shared/components/location-selector";
import { LocationSearchPicker } from "src/website-apps/shared/components/location-selector/LocationSearchPicker";
import { SheetOrPopper } from "src/website-apps/shared/components/location-selector/SheetOrPopper";
import { useGeocoder } from "src/website-apps/shared/components/location-selector/useGeocoder";
import { useGaFilter } from "./ga-filter-state";
import { buildLocationDisplayText, EMPTY_GRID_ITEMS, findClosestCitySuggestion, resolveVenueForCity } from "./ga-utils";
import {
  gaLocationRowStyle,
  gaLocationContentStyle,
  gaLocationTriggerRecipe,
  gaLocationIconStyle,
  gaLocationTextStyle,
} from "./styles";
const GA_LOCATION_PLACEMENT = "GA Festival Location Selector";

/**
 * GA location selector — flat text trigger with optional chevron.
 * Opens the standard LocationSearchPicker in a bottom sheet (mobile) or popover (desktop).
 *
 * Location changes are handled client-side via useGaFilter (instant filtering)
 * instead of triggering a backend grid refetch.
 *
 * When only one location is available, renders as static text (no chevron, no interaction).
 */
export const GALocationSelector: FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const model = usePerformerPageModelData();
  const filterCtx = useGaFilter();
  const { geocode, getPlacePredictions } = useGeocoder();

  const { citySuggestions, performerGridSurface: surface, seoCity, paidSearchQueryCity } = model;
  const hasMultipleLocations = (citySuggestions?.length ?? 0) > 1;

  // Get venue name from the first grid item matching the selected city
  const gridItems = surface?.type === "ga-festival" ? surface.eventsGrid.items : EMPTY_GRID_ITEMS;
  // effectiveCity is null for single-location festivals (no city filtering needed),
  // so we re-derive the display city from the same priority chain as useGaFilter —
  // this is intentional: display requires a city even when filtering is a no-op.
  const displayCity =
    filterCtx?.effectiveCity ??
    seoCity?.cityName ??
    paidSearchQueryCity?.cityName ??
    citySuggestions?.[0]?.city ??
    null;

  const classifications = surface?.type === "ga-festival" ? surface.gaPassClassifications : undefined;
  const venueForCity = useMemo(
    () => resolveVenueForCity(gridItems, displayCity, classifications),
    [gridItems, displayCity, classifications],
  );

  // Track user-selected city/country for display after picker selection
  const [userSelectedCityLabel, setUserSelectedCityLabel] = useState<string | null>(null);

  const displayText = useMemo(
    () => buildLocationDisplayText(venueForCity, userSelectedCityLabel, seoCity, paidSearchQueryCity, citySuggestions),
    [venueForCity, userSelectedCityLabel, seoCity, paidSearchQueryCity, citySuggestions],
  );

  // "Change location, currently {0}"
  const ariaLabel = useFormattedContent("cid$#4292182#", displayText);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleChange = useCallback(
    (location: Location) => {
      const matchedCity = findClosestCitySuggestion(citySuggestions, location.lt, location.lg, location.n);
      filterCtx?.setSelectedCity(matchedCity);
      setUserSelectedCityLabel([location.n, location.st, location.ct].filter(Boolean).join(", "));
      setIsOpen(false);
    },
    [filterCtx, citySuggestions],
  );

  const inputStyleCanParticipate = useCallback(() => hasMultipleLocations, [hasMultipleLocations]);
  const execInputStyle = useFeatureTernary(
    FeatureSetEnum.TMPerformerFestivalLocationSelectChip,
    inputStyleCanParticipate,
  );

  const isInputStyleVariant = execInputStyle({ control: () => false, variant: () => true });

  const currentCitySuggestion = useMemo(
    () => citySuggestions?.find((s) => s.city === displayCity),
    [citySuggestions, displayCity],
  );

  const syntheticLocation = useMemo<Location | undefined>(() => {
    if (!displayCity) return undefined;
    return {
      n: displayCity,
      lt: currentCitySuggestion?.lat ?? 0,
      lg: currentCitySuggestion?.long ?? 0,
      ct: currentCitySuggestion?.country ?? "",
      st: currentCitySuggestion?.state ?? "",
      src: LocationSourceType.USER_SELECTION,
    };
  }, [displayCity, currentCitySuggestion]);

  // Picker closes via its internal onClose after onChange — no explicit close needed here.
  const handleLocationSelectorChange = useCallback(
    (newLocation: Location) => {
      const matchedCity = findClosestCitySuggestion(citySuggestions, newLocation.lt, newLocation.lg, newLocation.n);
      filterCtx?.setSelectedCity(matchedCity);
      setUserSelectedCityLabel([newLocation.n, newLocation.st, newLocation.ct].filter(Boolean).join(", "));
    },
    [filterCtx, citySuggestions],
  );

  if (!displayText) return null;

  if (isInputStyleVariant) {
    return (
      <div className={gaLocationRowStyle} data-testid={DataTestIds.GaLocationSelector}>
        <LocationSelector
          location={syntheticLocation}
          onLocationChange={handleLocationSelectorChange}
          placeholder={displayText}
          overrideTextValue={displayText}
          useInputStyle
          applied
          placement={GA_LOCATION_PLACEMENT}
          includeUseMyLocation={false}
          hideSuggestedLocationsHeader
          wrapperClassName={gaLocationContentStyle}
          inputFitContent
          disableSearchInput
          showInputChevron
        />
      </div>
    );
  }

  const locationContent = (
    <>
      <span className={gaLocationIconStyle}>
        <LocationOutlineIcon size={16} />
      </span>
      <span className={gaLocationTextStyle} data-ph-mask>
        {displayText}
      </span>
      {hasMultipleLocations && (
        <span className={gaLocationIconStyle}>
          <ChevronDownIcon size={16} />
        </span>
      )}
    </>
  );

  return (
    <div className={gaLocationRowStyle} data-testid={DataTestIds.GaLocationSelector}>
      {hasMultipleLocations ? (
        <button
          type="button"
          className={gaLocationTriggerRecipe({ interaction: "interactive" })}
          onClick={() => {
            setIsOpen(true);
            setOpenCount((c) => c + 1);
          }}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-label={ariaLabel}
        >
          {locationContent}
        </button>
      ) : (
        <span className={gaLocationTriggerRecipe()}>{locationContent}</span>
      )}
      {isOpen && (
        <SheetOrPopper key={openCount} onClose={handleClose}>
          <LocationSearchPicker
            onClose={handleClose}
            geocode={geocode}
            getPlacePredictions={getPlacePredictions}
            onChange={handleChange}
            includeUseMyLocation={false}
            hideSearchInput
            embedded
          />
        </SheetOrPopper>
      )}
    </div>
  );
};
